import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClientErrorKind, ClientErrorPayload, SecurityStatus } from "@unbox/api";
import { AuthClientError, type AuthClientFailure } from "@/api/auth-client-error";
import { handleError } from "@/api/error";
import { authClient } from "@/lib/auth-client";
import { client } from "./client";

export type { SecurityStatus };

const securityKey = ["security"];

type AuthClientResult<T> = { data: T | null; error: AuthClientFailure | null };

const unwrap = <T>({ data, error }: AuthClientResult<T>): T => {
  if (error) throw new AuthClientError(error);
  if (!data) throw new AuthClientError({ status: 500, statusText: "No response" });
  return data;
};

export const isWrongPassword = (error: unknown) =>
  error instanceof AuthClientError && error.code === "INVALID_PASSWORD";

export const useSecurityStatus = () => {
  return useQuery({
    queryKey: securityKey,
    queryFn: async () => {
      const res = await client.user.security.$get();
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

const useInvalidateSecurity = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: securityKey });
};

export const useChangePassword = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationFn: async (json: { currentPassword: string; newPassword: string }) => {
      const res = await client.user.password.$post({ json });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
    meta: { suppressGlobalError: true },
  });
};

export const useStartTwoFactorSetup = () => {
  return useMutation({
    mutationFn: async (password: string) => unwrap(await authClient.twoFactor.enable({ password })),
    meta: { suppressGlobalError: true },
  });
};

export const useConfirmTwoFactorSetup = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationFn: async (code: string) => unwrap(await authClient.twoFactor.verifyTotp({ code })),
    onSuccess: invalidate,
    meta: { suppressGlobalError: true },
  });
};

export const useRegenerateBackupCodes = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationFn: async (password: string) => unwrap(await authClient.twoFactor.generateBackupCodes({ password })),
    onSuccess: invalidate,
    meta: { suppressGlobalError: true },
  });
};

export const useDisableTwoFactor = () => {
  const invalidate = useInvalidateSecurity();
  return useMutation({
    mutationFn: async (password: string) => unwrap(await authClient.twoFactor.disable({ password })),
    onSuccess: invalidate,
    meta: { suppressGlobalError: true },
  });
};
